'use client';
import { Dispatch, FormEventHandler, SetStateAction, useEffect, useState } from 'react';
import AmericanExpress from '@/customercenter-ui/icons/americanExpress';
import CircleIcon from '@/customercenter-ui/icons/circle';
import Discover from '@/customercenter-ui/icons/discover';
import ErrorIcon from '@/customercenter-ui/icons/error';
import CheckSuccessIcon from '@/customercenter-ui/icons/greenCircle';
import MasterCardIcon from '@/customercenter-ui/icons/masterCard';
import SuccessIcon from '@/customercenter-ui/icons/success';
import VisaIcon from '@/customercenter-ui/icons/visa';
import { mapLocaleToMeaningfulFormat } from '@/helpers/utils/i18n';

interface InputFieldProps {
  items?: string;
  id: string;
  type?: string;
  classname?: string;
  htmlFor?: string;
  value?: string;
  disabled?: boolean;
  label:
    | 'password'
    | 'email'
    | 'catalogEmail'
    | 'firstName'
    | 'lastName'
    | 'postalCode'
    | 'phone'
    | 'streetName'
    | 'city'
    | 'state'
    | 'additionalAddressInfo'
    | 'newPassword'
    | 'forgotEmail'
    | 'cardNumber'
    | 'expDate'
    | 'securityCode'
    | 'dob'
    | 'dpqDOB'
    | 'creditAccountId'
    | 'creditAccountIdOptional'
    | 'ssn'
    | 'country'
    | 'loginPassword'
    | 'currentPassword'
    | 'otherAmount'
    | 'catalogId'
    | 'batchNumber'
    | 'checkNumber'
    | 'totalCheckAmount'
    | 'cashAmount';
  errors?: any;
  register?: any;
  createAccountFlag?: boolean;
  focusFirstName?: boolean;
  focusCardNumber?: boolean;
  onChange?: any;
  drawerFlag?: boolean;
  cardType?: any;
  maxlength?: number;
  onPaste?: any;
  reseted?: boolean;
  setReseted?: Dispatch<SetStateAction<boolean>>;
  isNum?: boolean;
  handleKeyPress?: (event: React.KeyboardEvent) => void;
  preventClipboardEvents?: boolean;
  inputBorderColor?: string;
  placeholder?: string;
}

const InputField: React.FC<InputFieldProps> = ({
  items,
  id,
  htmlFor,
  type,
  classname,
  value,
  disabled,
  label,
  errors,
  register,
  createAccountFlag = false,
  focusFirstName = false,
  focusCardNumber = false,
  onChange,
  drawerFlag = false,
  cardType,
  maxlength,
  onPaste,
  reseted = false,
  setReseted,
  isNum,
  handleKeyPress,
  preventClipboardEvents,
  inputBorderColor = 'border-foggyGray',
  placeholder,
}) => {
  const [isFocus, setIsFocus] = useState(false);
  const [alphaValidated, setAlphaValidated] = useState(false);
  const [numberValidated, setNumberValidated] = useState(false);
  const [lengthValidated, setLengthValidated] = useState(false);
  const [focus, setFocus] = useState(false);

  useEffect(() => {
    if (focusFirstName || focusCardNumber) {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      document.getElementById(id)?.focus();
    }
  }, []);

  const handleChange = (value: any) => {
    const alpha = /(?=.*[a-zA-Z])/;
    const number = /(?=.*[0-9])/;
    const length = /^(?:\s*\S\s*){8,}$/;
    if (alpha.test(value)) {
      setAlphaValidated(true);
    } else {
      setAlphaValidated(false);
    }
    if (number.test(value)) {
      setNumberValidated(true);
    } else {
      setNumberValidated(false);
    }
    if (length.test(value)) {
      setLengthValidated(true);
    } else {
      setLengthValidated(false);
    }
  };

  const getCardIcon = (creditCardType: string) => {
    switch (creditCardType?.toLowerCase()) {
      case 'visa':
        return <VisaIcon className="gbomit h-5 w-9" />;
      case 'mastercard':
        return <MasterCardIcon className="gbomit h-5 w-9" />;
      case 'discover':
        return <Discover className="gbomit h-5 w-9" />;
      case 'amex':
        return <AmericanExpress className="gbomit h-5 w-9" />;
      default:
        return null;
    }
  };

  const handlSpecialeInput: FormEventHandler<HTMLFormElement> = (event) => {
    const input = event.currentTarget;
    let newValue = '';
    if (label === 'streetName' || label === 'additionalAddressInfo') {
      newValue = input?.value?.replace(/[^a-zA-Z0-9\s/#.,;:'\-\/\u00B0]+/g, '');
    } else if (id === 'firstName' || id === 'lastName') {
      newValue = input?.value?.replace(/[^a-zA-Z\s-]/g, '');
    } else if (label === 'postalCode') {
      newValue = input?.value?.replace(/[^a-zA-Z0-9\s-]/g, '');
    } else {
      newValue = input?.value?.replace(/[^a-zA-Z0-9\s]/g, '');
    }
    if (input?.value !== newValue) {
      input.value = newValue;
      const changeEvent = new Event('input', { bubbles: true });
      input.dispatchEvent(changeEvent);
    }
  };

  const specialCharsNotAllowedFields = [
    'firstName',
    'lastName',
    'companyName',
    'streetName',
    'line1',
    'line2',
    'addressLine2',
    'city',
    'additionalAddressInfo',
    'zip',
    'postalCode',
    'address',
  ];

  const handleSpecialChars = (event: any) => {
    if (specialCharsNotAllowedFields?.includes(label)) {
      handlSpecialeInput(event);
    }
  };

  const combinedKeyPressHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (
      isNum &&
      !/\d/.test(event.key) &&
      ![
        mapLocaleToMeaningfulFormat().backspace,
        mapLocaleToMeaningfulFormat().tab,
        mapLocaleToMeaningfulFormat().arrowLeft,
        mapLocaleToMeaningfulFormat().arrowRight,
      ].includes(event.key)
    ) {
      event.preventDefault();
    }
    if (handleKeyPress) {
      handleKeyPress(event);
    }
  };

  useEffect(() => {
    if (reseted) {
      setAlphaValidated(false);
      setLengthValidated(false);
      setNumberValidated(false);
      setFocus(false);
    }
  }, [reseted]);
  const isValidated = label === 'newPassword' ? lengthValidated && alphaValidated && numberValidated : true;

  const registerInput = typeof register === 'function' ? register : () => {};

  return (
    <div className={type === 'hidden' || label === 'otherAmount' ? '' : 'yourAddressBox mb-7'}>
      <div className="relative w-full">
        <input
          id={id}
          className={`
            peer block appearance-none rounded-lg border bg-transparent p-4 pr-6 text-base text-black lg:pr-10
            ${errors?.[label] ? 'border-2 border-scarletRed' : inputBorderColor}
            ${classname}
          `}
          maxLength={maxlength}
          placeholder={placeholder}
          value={value}
          preventclipboardevents={preventClipboardEvents ? 'true' : 'false'}
          type={type}
          disabled={disabled}
          onPaste={onPaste}
          {...registerInput(label, {
            ...(label === mapLocaleToMeaningfulFormat().inputNewPassword && {
              onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                e.preventDefault();
                handleSpecialChars(e);
                handleChange(e.target.value);
                e.currentTarget.value ? setIsFocus(true) : setIsFocus(false);
                setReseted?.(false);
              },
            }),
            ...(label !== mapLocaleToMeaningfulFormat().inputNewPassword && {
              onChange: (e: any) => {
                e.preventDefault();
                handleSpecialChars(e);
                onChange?.(e);
              },
              onBlur: (e: React.FormEvent<HTMLInputElement>) => {
                e.currentTarget.value ? setIsFocus(true) : setIsFocus(false);
              },
            }),
          })}
          onKeyPress={combinedKeyPressHandler}
          onFocus={() => {
            setFocus(true);
          }}
          onInput={handleSpecialChars}
        />
        <label
          htmlFor={htmlFor}
          className={
            'absolute start-1 top-1 ml-1 origin-[0] -translate-y-4 scale-75 transform bg-white px-2 text-black duration-300 peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:scale-100 peer-focus:top-2 peer-focus:-translate-y-4 peer-focus:scale-75 peer-focus:px-2 rtl:peer-focus:left-auto rtl:peer-focus:translate-x-1/4' +
            (errors?.[label] && ' text-scarletRed ')
          }
        >
          {items}
        </label>
        {errors && errors[label] ? (
          <section
            className={
              'absolute top-5 ' +
              (label === mapLocaleToMeaningfulFormat().inputPassword ||
              label === mapLocaleToMeaningfulFormat().inputNewPassword ||
              label === mapLocaleToMeaningfulFormat().inputLoginPassword ||
              label === mapLocaleToMeaningfulFormat().inputCurrentPassword
                ? createAccountFlag
                  ? ` right-24 ${!drawerFlag && 'md:right-16'}`
                  : ' right-16 '
                : label === mapLocaleToMeaningfulFormat().inputSecurityCode
                ? 'right-1'
                : label === mapLocaleToMeaningfulFormat().cardNumberLabel
                ? 'right-4'
                : 'right-1 lg:right-4')
            }
          >
            <ErrorIcon className="h-5 w-5" />
          </section>
        ) : (
          isFocus && (
            <section
              className={
                'absolute top-5 ' +
                (label === mapLocaleToMeaningfulFormat().inputPassword ||
                label === mapLocaleToMeaningfulFormat().inputNewPassword ||
                label === mapLocaleToMeaningfulFormat().inputLoginPassword ||
                label === mapLocaleToMeaningfulFormat().inputCurrentPassword
                  ? createAccountFlag
                    ? ` right-24 ${!drawerFlag && 'md:right-16'}`
                    : ' right-16 '
                  : label === mapLocaleToMeaningfulFormat().inputSecurityCode
                  ? 'right-1'
                  : label === mapLocaleToMeaningfulFormat().cardNumberLabel
                  ? 'right-4'
                  : 'right-1 lg:right-4')
              }
            >
              {label === mapLocaleToMeaningfulFormat().inputCardNumber && cardType !== '' && getCardIcon(cardType)}
              {label === mapLocaleToMeaningfulFormat().inputNewPassword && isValidated ? (
                <SuccessIcon className="h-5 w-5 bg-white" />
              ) : (
                <></>
              )}
              {(label === mapLocaleToMeaningfulFormat().inputDob ||
                label === mapLocaleToMeaningfulFormat().inputcreditAccountId ||
                label === mapLocaleToMeaningfulFormat().inputSsn) &&
              focus &&
              value ? (
                <SuccessIcon className="h-5 w-5 bg-white" />
              ) : (
                <></>
              )}
              {label !== mapLocaleToMeaningfulFormat().inputCardNumber &&
              label !== mapLocaleToMeaningfulFormat().inputNewPassword &&
              label !== mapLocaleToMeaningfulFormat().inputcreditAccountId &&
              label !== mapLocaleToMeaningfulFormat().inputSsn &&
              label !== mapLocaleToMeaningfulFormat().inputDob &&
              focus ? (
                <SuccessIcon className="h-5 w-5 bg-white" />
              ) : (
                <></>
              )}
            </section>
          )
        )}
      </div>
      {label !== mapLocaleToMeaningfulFormat().inputNewPassword && errors?.[label] ? (
        <p
          className={
            'ml-2 mt-1 text-sm text-scarletRed ' +
            (label === 'postalCode' ? 'flex-wrap' : '') +
            (drawerFlag ? '' : 'absolute')
          }
        >
          {errors[label]?.message}
        </p>
      ) : (
        <></>
      )}
      {label === mapLocaleToMeaningfulFormat().inputNewPassword && (
        <>
          {errors[label]?.message === mapLocaleToMeaningfulFormat().passwordRequired ||
          errors[label]?.message === mapLocaleToMeaningfulFormat().enterPassword ? (
            <p className={'ml-4 mt-1 text-sm text-scarletRed '}>{errors[label]?.message}</p>
          ) : (
            <></>
          )}
          {isFocus && !reseted ? (
            <section className="ml-4 mt-2">
              <p className="mb-1 text-base leading-5">{mapLocaleToMeaningfulFormat().passwordReq}</p>
              <section className="flex items-center gap-x-2 pl-4">
                <section>
                  {lengthValidated ? <CheckSuccessIcon className="h-4 w-4" /> : <CircleIcon className="h-4 w-4" />}
                </section>
                <section
                  className={`${lengthValidated ? 'font-semibold text-forestGreen' : 'text-black'} text-base leading-5`}
                >
                  {mapLocaleToMeaningfulFormat().eightCharacter}
                </section>
              </section>
              <section className="flex items-center gap-x-2 pl-4">
                <section>
                  {alphaValidated ? <CheckSuccessIcon className="h-4 w-4" /> : <CircleIcon className=" h-4 w-4" />}
                </section>
                <section
                  className={`${alphaValidated ? 'font-semibold text-forestGreen' : 'text-black'} text-base leading-5`}
                >
                  {mapLocaleToMeaningfulFormat().oneLetter}
                </section>
              </section>
              <section className="flex items-center gap-x-2 pl-4">
                <section>
                  {numberValidated ? <CheckSuccessIcon className="h-4 w-4" /> : <CircleIcon className=" h-4 w-4" />}
                </section>
                <section
                  className={`${numberValidated ? 'font-semibold text-forestGreen' : 'text-black'} text-base leading-5`}
                >
                  {mapLocaleToMeaningfulFormat().oneNumber}
                </section>
              </section>
            </section>
          ) : (
            <></>
          )}
        </>
      )}
    </div>
  );
};

export default InputField;
